import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Package, MapPin, Clock, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/services/apiClient';

const USNLookup = () => {
  const navigate = useNavigate();
  const [usnCode, setUsnCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleLookup = async (e) => {
    e?.preventDefault();
    const trimmed = usnCode.trim();
    if (!trimmed) return;

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await apiClient.get(`/wms/sfg/usn-lookup/${encodeURIComponent(trimmed)}`);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to look up USN');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setUsnCode('');
    setResult(null);
    setError('');
    inputRef.current?.focus();
  };

  const sourceLabel = {
    fg_put_away_slip: 'FG Put-Away Slip',
    wms_fg_inventory: 'FG Inventory',
    wms_sfg_inventory: 'SFG Inventory',
    dispatch_received_bundle_usns: 'Received (Not Put Away)'
  };

  const sourceColor = {
    fg_put_away_slip: 'bg-green-100 text-green-800 border-green-300',
    wms_fg_inventory: 'bg-blue-100 text-blue-800 border-blue-300',
    wms_sfg_inventory: 'bg-purple-100 text-purple-800 border-purple-300',
    dispatch_received_bundle_usns: 'bg-amber-100 text-amber-800 border-amber-300'
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/wms/sfg')}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            data-testid="usn-lookup-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Search className="w-7 h-7" />
            <div>
              <h1 className="text-xl font-bold">USN Lookup</h1>
              <p className="text-sm text-[#E8DCC8]">Find put-away slip by USN</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Search Input */}
        <form onSubmit={handleLookup} className="flex gap-2" data-testid="usn-lookup-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              autoFocus
              type="text"
              placeholder="Scan or enter USN code..."
              value={usnCode}
              onChange={(e) => setUsnCode(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="usn-lookup-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading || !usnCode.trim()}
            className="px-6 py-3 bg-[#1A1F2E] text-white hover:bg-[#2A2F3E]"
            data-testid="usn-lookup-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
          </Button>
          {result && (
            <Button type="button" variant="outline" onClick={handleClear} className="px-4 py-3" data-testid="usn-lookup-clear">
              Clear
            </Button>
          )}
        </form>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700" data-testid="usn-lookup-error">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4" data-testid="usn-lookup-result">
            {/* Source Badge */}
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${sourceColor[result.source] || 'bg-gray-100 text-gray-700'}`}>
                {sourceLabel[result.source] || result.source}
              </span>
              {result.note && <span className="text-sm text-gray-500">{result.note}</span>}
            </div>

            {!result.found ? (
              <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center" data-testid="usn-not-found">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">USN not found</p>
                <p className="text-sm text-gray-400 mt-1">This USN does not exist in any collection</p>
              </div>
            ) : (
              result.results.map((item, idx) => (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-testid={`usn-result-card-${idx}`}>
                  {/* Put-away slip result */}
                  {result.source === 'fg_put_away_slip' && (
                    <div className="divide-y divide-gray-100">
                      <div className="p-4 bg-green-50">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-green-800 text-lg" data-testid="result-slip-id">{item.slip_id}</span>
                        </div>
                      </div>
                      <div className="p-4 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Bin Location</p>
                          <p className="text-base font-semibold mt-1 flex items-center gap-1" data-testid="result-bin">
                            <MapPin className="w-4 h-4 text-gray-400" /> {item.bin_code || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Bundle Code</p>
                          <p className="text-base font-semibold mt-1 flex items-center gap-1" data-testid="result-bundle">
                            <Package className="w-4 h-4 text-gray-400" /> {item.bundle_code || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Placed At</p>
                          <p className="text-base font-semibold mt-1 flex items-center gap-1" data-testid="result-placed-at">
                            <Clock className="w-4 h-4 text-gray-400" />
                            {item.placed_at ? new Date(item.placed_at).toLocaleString('en-IN') : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Slip Status</p>
                          <p className="text-base font-semibold mt-1">{item.status || '-'}</p>
                        </div>
                        {item.assigned_to_name && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Put Away By</p>
                            <p className="text-base font-semibold mt-1">{item.assigned_to_name}</p>
                          </div>
                        )}
                        {item.item_barcode && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Barcode</p>
                            <p className="text-base font-semibold mt-1">{item.item_barcode}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* FG Inventory result */}
                  {result.source === 'wms_fg_inventory' && (
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Bin</p>
                          <p className="font-semibold flex items-center gap-1"><MapPin className="w-4 h-4 text-gray-400" /> {item.bin_code || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Bundle</p>
                          <p className="font-semibold flex items-center gap-1"><Package className="w-4 h-4 text-gray-400" /> {item.bundle_code || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Status</p>
                          <p className="font-semibold">{item.status || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Slip ID</p>
                          <p className="font-semibold">{item.put_away_slip_id || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SFG Inventory result */}
                  {result.source === 'wms_sfg_inventory' && (
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Bin</p>
                          <p className="font-semibold flex items-center gap-1"><MapPin className="w-4 h-4 text-gray-400" /> {item.bin_location || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Bundle</p>
                          <p className="font-semibold flex items-center gap-1"><Package className="w-4 h-4 text-gray-400" /> {item.bundle_code || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Status</p>
                          <p className="font-semibold">{item.status || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">SKU</p>
                          <p className="font-semibold">{item.sku_code || item.item_barcode || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Received only */}
                  {result.source === 'dispatch_received_bundle_usns' && (
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Bundle</p>
                          <p className="font-semibold flex items-center gap-1"><Package className="w-4 h-4 text-gray-400" /> {item.bundle_code || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Design</p>
                          <p className="font-semibold">{item.design_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Barcode</p>
                          <p className="font-semibold">{item.item_barcode || '-'}</p>
                        </div>
                      </div>
                      {item.put_away_slips && item.put_away_slips.length > 0 && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs text-blue-600 uppercase font-medium mb-2">Put-Away Slip Generated</p>
                          {item.put_away_slips.map((slip, si) => (
                            <div key={si} className="flex items-center gap-3 text-sm">
                              <FileText className="w-4 h-4 text-blue-500" />
                              <span className="font-semibold text-blue-800" data-testid={`result-slip-${si}`}>{slip.slip_id}</span>
                              <span className="text-gray-500">({slip.usn_count} pcs)</span>
                              {slip.processed_at && (
                                <span className="text-gray-400 text-xs">{new Date(slip.processed_at).toLocaleString('en-IN')}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default USNLookup;
